import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';

// --- Type Definitions ---
interface PillarScore {
  pillarName: string;
  score: number;
  rating: string;
  summary: string;
}

interface PerformanceDiagnostic {
  id: string;
  title: string;
  description: string;
  impact: string;
  recommendation: string;
  pillarRelated: string;
}

interface ArchitectureSuggestionItem {
  description: string;
  awsServices: string[];
}

interface RiskImpactItem {
  id: string;
  riskLevel: 'High' | 'Moderate' | 'Low';
  finding: string;
  pillarRelated: string;
  businessImpact: string;
  remediationBenefit: string;
}

interface ReviewResults {
  reviewId: string;
  status: 'completed';
  overallRating: string;
  pillarScores: PillarScore[];
  performanceDiagnostics: PerformanceDiagnostic[];
  architectureSuggestions: {
    currentArchitectureDescription: string;
    recommendedArchitectureDescription: string;
    recommendationsList: ArchitectureSuggestionItem[];
  };
  riskImpactReport: RiskImpactItem[];
  loadTestResults?: {
    responseTimeMs: number;
    geographicalImpact: string;
    details: string;
  };
}

interface FormData {
  workloadName: string;
  applicationType: string;
  description: string;
  primaryAwsRegion: string;
  trafficVolume: string;
  awsServicesInUse: string[];
  availabilityZoneStrategy: string;
  autoScalingEnabled: boolean;
  autoScalingMetrics: string[];
  loadBalancerEnabled: boolean;
  disasterRecoveryStrategy: boolean;
  coLocatedAppDb: boolean;
  cloudwatchExtensiveUse: string;
  centralizedLogging: boolean;
  iamBestPractices: string;
  costSavingMechanisms: boolean;
  publicUrl: string;
}

// --- Styles (Inline for single file) ---
const styles = {
  container: {
    fontFamily: 'Arial, sans-serif',
    maxWidth: '900px',
    margin: '40px auto',
    padding: '20px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '8px',
    backgroundColor: '#fff',
    lineHeight: '1.6',
    color: '#333',
  },
  header: {
    textAlign: 'center',
    color: '#0056b3',
    marginBottom: '30px',
    fontSize: '2.2em',
    fontWeight: 'bold',
  },
  subHeader: {
    textAlign: 'center',
    color: '#555',
    fontSize: '1.1em',
    marginBottom: '20px',
  },
  button: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '12px 25px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '1em',
    transition: 'background-color 0.2s',
    display: 'block',
    margin: '20px auto',
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '0.9em',
    transition: 'background-color 0.2s',
    margin: '10px 5px',
  },
  ctaButton: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '15px 30px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1.1em',
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    margin: '25px auto 10px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    color: '#444',
  },
  input: {
    width: 'calc(100% - 20px)',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1em',
  },
  textarea: {
    width: 'calc(100% - 20px)',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1em',
    minHeight: '80px',
    resize: 'vertical',
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1em',
    backgroundColor: '#f9f9f9',
  },
  checkboxGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  },
  progressBar: {
    height: '10px',
    backgroundColor: '#e0e0e0',
    borderRadius: '5px',
    marginBottom: '20px',
  },
  progressBarFill: (width: number) => ({
    width: `${width}%`,
    height: '100%',
    backgroundColor: '#007bff',
    borderRadius: '5px',
    transition: 'width 0.5s ease-in-out',
  }),
  loadingContainer: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '1.2em',
    color: '#007bff',
  },
  spinner: {
    border: '4px solid rgba(0, 0, 0, 0.1)',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    borderLeftColor: '#007bff',
    animation: 'spin 1s ease infinite',
    margin: '20px auto',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  resultsSection: {
    marginBottom: '40px',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    borderLeft: '5px solid #007bff',
  },
  resultsSectionTitle: {
    color: '#0056b3',
    fontSize: '1.8em',
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
  },
  pillarScoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  pillarCard: {
    backgroundColor: '#fff',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  },
  pillarScore: (score: number) => ({
    fontSize: '2.5em',
    fontWeight: 'bold',
    color: score >= 70 ? '#28a745' : score >= 50 ? '#ffc107' : '#dc3545',
    marginBottom: '5px',
  }),
  pillarRating: {
    fontSize: '1.1em',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  pillarSummary: {
    fontSize: '0.9em',
    color: '#666',
  },
  findingItem: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
    borderLeft: '4px solid #ffc107',
  },
  findingTitle: {
    fontWeight: 'bold',
    color: '#0056b3',
    fontSize: '1.2em',
    marginBottom: '8px',
  },
  findingDescription: {
    marginBottom: '5px',
  },
  riskLevelHigh: { color: '#dc3545', fontWeight: 'bold' },
  riskLevelModerate: { color: '#ffc107', fontWeight: 'bold' },
  riskLevelLow: { color: '#28a745', fontWeight: 'bold' },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
    width: '90%',
    maxWidth: '500px',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '1.5em',
    cursor: 'pointer',
    color: '#666',
  },
  modalTitle: {
    color: '#0056b3',
    fontSize: '1.8em',
    marginBottom: '20px',
    textAlign: 'center',
  },
  modalInput: {
    width: 'calc(100% - 22px)', // Account for padding + border
    padding: '12px',
    marginBottom: '15px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1em',
  },
  modalButton: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '12px 25px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '1em',
    transition: 'background-color 0.2s',
    width: '100%',
  },
  smallText: {
    fontSize: '0.85em',
    color: '#666',
    marginTop: '10px',
  },
};

// --- Data for Form Options ---
const applicationTypes = [
  'Web Application', 'API Service', 'Data Processing', 'Batch Job',
  'Internal Tool', 'Mobile Backend', 'Gaming Backend', 'IoT Platform', 'Other'
];

const awsRegions = [
  'us-east-1 (N. Virginia)', 'us-east-2 (Ohio)', 'us-west-1 (N. California)',
  'us-west-2 (Oregon)', 'eu-west-1 (Ireland)', 'eu-central-1 (Frankfurt)',
  'ap-southeast-1 (Singapore)', 'ap-southeast-2 (Sydney)', 'ap-northeast-1 (Tokyo)'
];

const trafficVolumes = [
  'Low (hundreds/day)', 'Medium (thousands/day)', 'High (millions/day)', 'Sporadic/Burst'
];

const allAwsServices = [
  'EC2', 'RDS', 'S3', 'Lambda', 'API Gateway', 'DynamoDB', 'VPC', 'ELB/ALB/NLB',
  'Auto Scaling', 'CloudFront', 'Route 53', 'SQS', 'SNS', 'ECS/EKS', 'CloudWatch',
  'IAM', 'SageMaker', 'Redshift', 'Glue', 'Step Functions', 'Kinesis', 'Cognito'
];

const autoScalingMetricsOptions = [
  'CPU Utilization', 'Network In', 'Request Count', 'Queue Length', 'Custom Metric'
];

// --- Simulated API Responses (Hardcoded for this example) ---

const simulatedProcessingDelay = 3000; // 3 seconds
const simulatedResults: ReviewResults = {
  reviewId: 'simulated-review-id-123',
  status: 'completed',
  overallRating: 'Needs Improvement',
  pillarScores: [
    { pillarName: 'Operational Excellence', score: 65, rating: 'Good', summary: 'Solid foundation in monitoring, but logging could be more centralized.' },
    { pillarName: 'Security', score: 70, rating: 'Good', summary: 'Good IAM practices, but review security group configurations.' },
    { pillarName: 'Reliability', score: 40, rating: 'Needs Improvement', summary: 'High risk due to single AZ deployment and lack of failover.' },
    { pillarName: 'Performance Efficiency', score: 55, rating: 'Needs Improvement', summary: 'Potential bottlenecks due to co-located servers and no CDN.' },
    { pillarName: 'Cost Optimization', score: 30, rating: 'High Risk', summary: 'Significant cost-saving opportunities by leveraging RIs/Savings Plans.' },
  ],
  performanceDiagnostics: [
    {
      id: 'perf-1',
      title: 'High Latency Risk: No CDN in Use',
      description: 'Users distant from your primary AWS region may experience significant latency due to the absence of a Content Delivery Network (CDN) like AWS CloudFront.',
      impact: 'Reduced user experience, potential abandonment of services by distant users.',
      recommendation: 'Implement AWS CloudFront for static content delivery and potentially dynamic content acceleration.',
      pillarRelated: 'Performance Efficiency',
    },
    {
      id: 'perf-2',
      title: 'Co-located Database and Application Servers',
      description: 'Your database and application servers appear to be running on the same instance. This creates a single point of failure and scalability limitation.',
      impact: 'Performance degradation during high load, difficult to scale components independently, higher risk of downtime.',
      recommendation: 'Decouple your database into a managed service like AWS RDS (Multi-AZ for high availability) and deploy application servers in an Auto Scaling Group.',
      pillarRelated: 'Performance Efficiency, Reliability',
    },
    {
      id: 'perf-3',
      title: 'No Serverless or Managed Services for Compute',
      description: 'Your application primarily relies on EC2 instances for compute. While valid, consider serverless (Lambda) or container (ECS/EKS) options for potentially higher efficiency and lower operational overhead.',
      impact: 'Higher operational costs, manual scaling efforts, potential underutilization of resources.',
      recommendation: 'Evaluate migrating suitable workloads to AWS Lambda or container services (ECS/EKS) for improved cost-efficiency and automatic scaling.',
      pillarRelated: 'Performance Efficiency, Cost Optimization, Operational Excellence',
    },
  ],
  architectureSuggestions: {
    currentArchitectureDescription: 'Your current architecture: Single EC2 instance serving both web application and database.',
    recommendedArchitectureDescription: 'Consider evolving to a more resilient and scalable tiered architecture with:',
    recommendationsList: [
      { description: 'Application Load Balancer (ALB) distributing traffic.', awsServices: ['ELB/ALB'] },
      { description: 'Auto Scaling Group for web servers across multiple Availability Zones.', awsServices: ['EC2', 'Auto Scaling'] },
      { description: 'Managed RDS database with Multi-AZ for high availability and automated backups.', awsServices: ['RDS'] },
      { description: 'Amazon S3 for static assets and CloudFront for global content delivery.', awsServices: ['S3', 'CloudFront'] },
      { description: 'Utilize SQS/SNS for decoupling microservices or asynchronous tasks.', awsServices: ['SQS', 'SNS'] },
      { description: 'Centralized logging and monitoring with CloudWatch Logs and Dashboards.', awsServices: ['CloudWatch'] },
    ],
  },
  riskImpactReport: [
    {
      id: 'risk-1',
      riskLevel: 'High',
      finding: 'Single Point of Failure: No failover for instances.',
      pillarRelated: 'Reliability',
      businessImpact: 'Potential for significant downtime during outages, hardware failures, or high traffic spikes, leading to revenue loss and customer dissatisfaction.',
      remediationBenefit: 'Implementing Multi-AZ deployment and Auto Scaling can significantly improve uptime and ensure continuous service delivery during disruptions.',
    },
    {
      id: 'risk-2',
      riskLevel: 'Moderate',
      finding: 'No automated backups for database.',
      pillarRelated: 'Reliability',
      businessImpact: 'Risk of data loss in case of database corruption or accidental deletion, leading to loss of critical business data and extended recovery times.',
      remediationBenefit: 'Enabling automated backups with AWS RDS ensures point-in-time recovery and minimizes data loss, reducing recovery time objectives (RTO).',
    },
    {
      id: 'risk-3',
      riskLevel: 'Low',
      finding: 'No Reserved Instances or Savings Plans.',
      pillarRelated: 'Cost Optimization',
      businessImpact: 'Higher monthly AWS expenditure compared to optimized setups, impacting profitability and budget efficiency.',
      remediationBenefit: 'Committing to Reserved Instances or Savings Plans can reduce compute costs by 30-70% compared to On-Demand pricing, leading to significant long-term savings.',
    },
  ],
  loadTestResults: {
    responseTimeMs: 1250,
    geographicalImpact: 'High latency observed for users in APAC regions due to origin server location.',
    details: 'Initial page load was 1.25s. Largest contributors were unoptimized image assets and distant server response.',
  },
};

// --- Main App Component ---
const App: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'INTRO' | 'FORM_STEP_1' | 'FORM_STEP_2' | 'FORM_STEP_3' | 'LOADING_RESULTS' | 'SHOW_RESULTS'>(
    'INTRO'
  );
  const totalFormSteps = 3; // Number of questionnaire steps

  const [formData, setFormData] = useState<FormData>({
    workloadName: '',
    applicationType: '',
    description: '',
    primaryAwsRegion: '',
    trafficVolume: '',
    awsServicesInUse: [],
    availabilityZoneStrategy: '',
    autoScalingEnabled: false,
    autoScalingMetrics: [],
    loadBalancerEnabled: false,
    disasterRecoveryStrategy: false,
    coLocatedAppDb: false,
    cloudwatchExtensiveUse: '',
    centralizedLogging: false,
    iamBestPractices: '',
    costSavingMechanisms: false,
    publicUrl: '',
  });

  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewResults, setReviewResults] = useState<ReviewResults | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [showConsultationModal, setShowConsultationModal] = useState<boolean>(false);
  const [leadEmail, setLeadEmail] = useState<string>('');
  const [leadName, setLeadName] = useState<string>('');
  const [leadPhone, setLeadPhone] = useState<string>('');
  const [leadMessage, setLeadMessage] = useState<string>('');
  const [leadFormStatus, setLeadFormStatus] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (type === 'checkbox' && name === 'awsServicesInUse') {
      setFormData(prev => ({
        ...prev,
        awsServicesInUse: checked
          ? [...prev.awsServicesInUse, value]
          : prev.awsServicesInUse.filter(service => service !== value),
      }));
    } else if (type === 'checkbox' && name === 'autoScalingMetrics') {
      setFormData(prev => ({
        ...prev,
        autoScalingMetrics: checked
          ? [...prev.autoScalingMetrics, value]
          : prev.autoScalingMetrics.filter(metric => metric !== value),
      }));
    } else if (type === 'radio' && ['autoScalingEnabled', 'loadBalancerEnabled', 'disasterRecoveryStrategy', 'coLocatedAppDb', 'centralizedLogging', 'costSavingMechanisms'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        [name]: value === 'true' // Convert string 'true'/'false' to boolean
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const goToNextStep = () => {
    switch (currentStep) {
      case 'INTRO':
        setCurrentStep('FORM_STEP_1');
        break;
      case 'FORM_STEP_1':
        setCurrentStep('FORM_STEP_2');
        break;
      case 'FORM_STEP_2':
        setCurrentStep('FORM_STEP_3');
        break;
      case 'FORM_STEP_3':
        handleSubmitWorkload(); // Submit on the last form step
        break;
      default:
        break;
    }
  };

  const goToPreviousStep = () => {
    switch (currentStep) {
      case 'FORM_STEP_1':
        setCurrentStep('INTRO');
        break;
      case 'FORM_STEP_2':
        setCurrentStep('FORM_STEP_1');
        break;
      case 'FORM_STEP_3':
        setCurrentStep('FORM_STEP_2');
        break;
      default:
        break;
    }
  };

  const handleSubmitWorkload = async () => {
    setIsLoading(true);
    setApiError(null);
    setCurrentStep('LOADING_RESULTS'); // Move to loading state immediately

    // Simulate API call to /review
    try {
      // In a real app:
      // const response = await fetch('/api/review', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData),
      // });
      // const data = await response.json();
      // if (!response.ok) throw new Error(data.message || 'Failed to submit review');

      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network delay

      const simulatedReviewId = 'simulated-review-id-123'; // Replace with actual reviewId from backend
      setReviewId(simulatedReviewId);

      // Immediately trigger fetching results, as the backend will now be processing
      fetchResults(simulatedReviewId);

    } catch (error: any) {
      setApiError(error.message || 'Error submitting workload.');
      setIsLoading(false);
      setCurrentStep('FORM_STEP_3'); // Go back to last form step on error
    }
  };

  const fetchResults = async (id: string) => {
    setIsLoading(true);
    setApiError(null);

    // Simulate polling for results
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setApiError('Results took too long to process. Please try again.');
        setIsLoading(false);
        setCurrentStep('INTRO'); // Go back to start
        return;
      }

      attempts++;
      console.log(`Attempt ${attempts} to fetch results for ${id}`);

      try {
        // In a real app:
        // const response = await fetch(`/api/review/${id}/results`);
        // const data = await response.json();

        await new Promise(resolve => setTimeout(resolve, simulatedProcessingDelay)); // Simulate backend processing time

        if (simulatedResults.status === 'completed') { // Check if backend reports 'completed'
          setReviewResults(simulatedResults); // Use the hardcoded results
          setCurrentStep('SHOW_RESULTS');
          setIsLoading(false);
        } else {
          // If backend reports 'processing' or other non-completed status
          setTimeout(poll, simulatedProcessingDelay); // Poll again
        }
      } catch (error: any) {
        setApiError(error.message || 'Error fetching results.');
        setIsLoading(false);
        setCurrentStep('INTRO');
      }
    };

    poll(); // Start polling
  };

  const handleDownloadReport = (e: FormEvent) => {
    e.preventDefault();
    setLeadFormStatus(null);
    setIsLoading(true);

    // Simulate API call to /report/download
    setTimeout(() => {
      // In a real app, this would send leadEmail and reviewId to backend
      console.log(`Simulating download report for email: ${leadEmail}, reviewId: ${reviewId}`);
      setIsLoading(false);
      setLeadFormStatus('success');
      // Optionally provide a direct download link here if the backend generated it immediately
    }, 1500);
  };

  const handleConsultationRequest = (e: FormEvent) => {
    e.preventDefault();
    setLeadFormStatus(null);
    setIsLoading(true);

    // Simulate API call to /consultation/schedule
    setTimeout(() => {
      // In a real app, this would send leadName, leadEmail, leadPhone, leadMessage, reviewId to backend
      console.log(`Simulating consultation request for: ${leadName} (${leadEmail}), reviewId: ${reviewId}`);
      setIsLoading(false);
      setLeadFormStatus('success');
    }, 1500);
  };

  const renderProgressBar = () => {
    let currentFormStep = 0;
    if (currentStep === 'FORM_STEP_1') currentFormStep = 1;
    if (currentStep === 'FORM_STEP_2') currentFormStep = 2;
    if (currentStep === 'FORM_STEP_3') currentFormStep = 3;

    const progress = (currentFormStep / totalFormSteps) * 100;

    if (currentFormStep === 0) return null; // Don't show on intro or results page

    return (
      <div style={styles.progressBar}>
        <div style={styles.progressBarFill(progress)}></div>
        <p style={{ textAlign: 'center', fontSize: '0.9em', color: '#666' }}>
          Step {currentFormStep} of {totalFormSteps}
        </p>
      </div>
    );
  };

  // --- Render Logic for Different Steps ---
  return (
    <div style={styles.container}>
      {/* Global CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Intro Page */}
      {currentStep === 'INTRO' && (
        <div>
          <h1 style={styles.header}>Unlock Your AWS Potential: Free Architecture & Performance Review</h1>
          <p style={styles.subHeader}>
            Evaluate your AWS setup against best practices for reliability, efficiency, and cost optimization.
          </p>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '30px' }}>
            <li>Identify performance bottlenecks and cost savings.</li>
            <li>Discover areas for improved reliability and scalability.</li>
            <li>Receive actionable recommendations for cloud-native design.</li>
            <li>Gain insights typically provided by an AWS Architect.</li>
          </ul>
          <h2 style={{ textAlign: 'center', color: '#0056b3', marginBottom: '20px' }}>How It Works:</h2>
          <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ flexBasis: '30%' }}>
              <h3>1. Answer Questions</h3>
              <p>Tell us about your AWS workload or application.</p>
            </div>
            <div style={{ flexBasis: '30%' }}>
              <h3>2. Get Instant Scores</h3>
              <p>Receive ratings across AWS Well-Architected Pillars.</p>
            </div>
            <div style={{ flexBasis: '30%' }}>
              <h3>3. Review Recommendations</h3>
              <p>Obtain a detailed report and actionable steps.</p>
            </div>
          </div>
          <button style={styles.button} onClick={() => setCurrentStep('FORM_STEP_1')}>
            Start Your Free Review
          </button>
        </div>
      )}

      {/* Questionnaire Form Steps */}
      {(currentStep === 'FORM_STEP_1' || currentStep === 'FORM_STEP_2' || currentStep === 'FORM_STEP_3') && (
        <form onSubmit={(e) => { e.preventDefault(); goToNextStep(); }}>
          <h1 style={styles.header}>Workload Questionnaire</h1>
          {renderProgressBar()}
          {apiError && <p style={{ color: 'red', textAlign: 'center' }}>{apiError}</p>}

          {/* Step 1: Workload Details */}
          {currentStep === 'FORM_STEP_1' && (
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="workloadName">Workload/Application Name:</label>
                <input
                  type="text"
                  id="workloadName"
                  name="workloadName"
                  value={formData.workloadName}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="applicationType">Application Type:</label>
                <select
                  id="applicationType"
                  name="applicationType"
                  value={formData.applicationType}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select an application type</option>
                  {applicationTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="description">Brief Description:</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  style={styles.textarea}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="primaryAwsRegion">Primary AWS Region:</label>
                <select
                  id="primaryAwsRegion"
                  name="primaryAwsRegion"
                  value={formData.primaryAwsRegion}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select a region</option>
                  {awsRegions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="trafficVolume">Estimated Traffic Volume:</label>
                <select
                  id="trafficVolume"
                  name="trafficVolume"
                  value={formData.trafficVolume}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select volume</option>
                  {trafficVolumes.map(volume => (
                    <option key={volume} value={volume}>{volume}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: AWS Services In Use */}
          {currentStep === 'FORM_STEP_2' && (
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>What AWS services does this workload primarily utilize?</label>
                <div style={styles.checkboxGroup}>
                  {allAwsServices.map(service => (
                    <div key={service} style={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        id={`service-${service}`}
                        name="awsServicesInUse"
                        value={service}
                        checked={formData.awsServicesInUse.includes(service)}
                        onChange={handleInputChange}
                      />
                      <label htmlFor={`service-${service}`}>{service}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="publicUrl">Optional: Public URL for basic load time test (e.g., https://www.yourdomain.com):</label>
                <input
                  type="url"
                  id="publicUrl"
                  name="publicUrl"
                  value={formData.publicUrl}
                  onChange={handleInputChange}
                  style={styles.input}
                  placeholder="https://your-application-url.com"
                />
              </div>
            </div>
          )}

          {/* Step 3: Deployment & Scalability, Monitoring & Cost */}
          {currentStep === 'FORM_STEP_3' && (
            <div>
              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>How is your application deployed across Availability Zones (AZs)?</legend>
                <div>
                  <input type="radio" id="singleAz" name="availabilityZoneStrategy" value="Single AZ" checked={formData.availabilityZoneStrategy === 'Single AZ'} onChange={handleInputChange} required />
                  <label htmlFor="singleAz">Single AZ</label>
                </div>
                <div>
                  <input type="radio" id="multiAzPrimaryStandby" name="availabilityZoneStrategy" value="Multi-AZ (primary/standby)" checked={formData.availabilityZoneStrategy === 'Multi-AZ (primary/standby)'} onChange={handleInputChange} />
                  <label htmlFor="multiAzPrimaryStandby">Multi-AZ (primary/standby)</label>
                </div>
                <div>
                  <input type="radio" id="multiAzActiveActive" name="availabilityZoneStrategy" value="Multi-AZ (active/active across AZs)" checked={formData.availabilityZoneStrategy === 'Multi-AZ (active/active across AZs)'} onChange={handleInputChange} />
                  <label htmlFor="multiAzActiveActive">Multi-AZ (active/active across AZs)</label>
                </div>
              </fieldset>

              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>Do you use Auto Scaling for your compute resources (e.g., EC2 instances)?</legend>
                <div>
                  <input type="radio" id="asYes" name="autoScalingEnabled" value="true" checked={formData.autoScalingEnabled === true} onChange={handleInputChange} />
                  <label htmlFor="asYes">Yes</label>
                </div>
                <div>
                  <input type="radio" id="asNo" name="autoScalingEnabled" value="false" checked={formData.autoScalingEnabled === false} onChange={handleInputChange} />
                  <label htmlFor="asNo">No</label>
                </div>
                {formData.autoScalingEnabled && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={styles.label}>Based on what metrics?</label>
                    <div style={styles.checkboxGroup}>
                      {autoScalingMetricsOptions.map(metric => (
                        <div key={metric} style={styles.checkboxItem}>
                          <input
                            type="checkbox"
                            id={`metric-${metric}`}
                            name="autoScalingMetrics"
                            value={metric}
                            checked={formData.autoScalingMetrics.includes(metric)}
                            onChange={handleInputChange}
                          />
                          <label htmlFor={`metric-${metric}`}>{metric}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </fieldset>

              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>Is a Load Balancer (ELB/ALB/NLB) in front of your application?</legend>
                <div>
                  <input type="radio" id="lbYes" name="loadBalancerEnabled" value="true" checked={formData.loadBalancerEnabled === true} onChange={handleInputChange} />
                  <label htmlFor="lbYes">Yes</label>
                </div>
                <div>
                  <input type="radio" id="lbNo" name="loadBalancerEnabled" value="false" checked={formData.loadBalancerEnabled === false} onChange={handleInputChange} />
                  <label htmlFor="lbNo">No</label>
                </div>
              </fieldset>

              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>Are your database and application servers co-located on a single instance?</legend>
                <div>
                  <input type="radio" id="coLocatedYes" name="coLocatedAppDb" value="true" checked={formData.coLocatedAppDb === true} onChange={handleInputChange} />
                  <label htmlFor="coLocatedYes">Yes</label>
                </div>
                <div>
                  <input type="radio" id="coLocatedNo" name="coLocatedAppDb" value="false" checked={formData.coLocatedAppDb === false} onChange={handleInputChange} />
                  <label htmlFor="coLocatedNo">No</label>
                </div>
              </fieldset>

              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>Do you extensively use AWS CloudWatch for monitoring and alarming?</legend>
                <div>
                  <input type="radio" id="cwYes" name="cloudwatchExtensiveUse" value="Yes" checked={formData.cloudwatchExtensiveUse === 'Yes'} onChange={handleInputChange} />
                  <label htmlFor="cwYes">Yes</label>
                </div>
                <div>
                  <input type="radio" id="cwPartial" name="cloudwatchExtensiveUse" value="Partially" checked={formData.cloudwatchExtensiveUse === 'Partially'} onChange={handleInputChange} />
                  <label htmlFor="cwPartial">Partially</label>
                </div>
                <div>
                  <input type="radio" id="cwNo" name="cloudwatchExtensiveUse" value="No" checked={formData.cloudwatchExtensiveUse === 'No'} onChange={handleInputChange} />
                  <label htmlFor="cwNo">No</label>
                </div>
              </fieldset>

              <fieldset style={styles.formGroup}>
                <legend style={styles.label}>Do you leverage cost-saving mechanisms like Reserved Instances or Savings Plans?</legend>
                <div>
                  <input type="radio" id="costSaveYes" name="costSavingMechanisms" value="true" checked={formData.costSavingMechanisms === true} onChange={handleInputChange} />
                  <label htmlFor="costSaveYes">Yes</label>
                </div>
                <div>
                  <input type="radio" id="costSaveNo" name="costSavingMechanisms" value="false" checked={formData.costSavingMechanisms === false} onChange={handleInputChange} />
                  <label htmlFor="costSaveNo">No</label>
                </div>
                <div>
                  <input type="radio" id="costSaveNotSure" name="costSavingMechanisms" value="not_sure" checked={formData.costSavingMechanisms === false && !['true', 'false'].includes(String(formData.costSavingMechanisms))} onChange={handleInputChange} />
                  <label htmlFor="costSaveNotSure">Not sure</label>
                </div>
              </fieldset>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
            {(currentStep === 'FORM_STEP_2' || currentStep === 'FORM_STEP_3') && (
              <button type="button" style={styles.secondaryButton} onClick={goToPreviousStep}>
                Back
              </button>
            )}
            <button type="submit" style={styles.button}>
              {currentStep === `FORM_STEP_${totalFormSteps}` ? 'Submit & Get Results' : 'Next'}
            </button>
          </div>
        </form>
      )}

      {/* Loading Results Page */}
      {currentStep === 'LOADING_RESULTS' && (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <h2>Analyzing Your AWS Architecture...</h2>
          <p>Please wait while we generate your custom review.</p>
          {apiError && <p style={{ color: 'red', marginTop: '20px' }}>{apiError}</p>}
        </div>
      )}

      {/* Show Results Page */}
      {currentStep === 'SHOW_RESULTS' && reviewResults && (
        <div>
          <h1 style={styles.header}>Your AWS Architecture Review Results</h1>
          <p style={styles.subHeader}>For: {formData.workloadName}</p>

          {/* Well-Architected Pillar Scores */}
          <div style={styles.resultsSection}>
            <h2 style={styles.resultsSectionTitle}>Well-Architected Pillar Scores</h2>
            <div style={styles.pillarScoreGrid}>
              {reviewResults.pillarScores.map(pillar => (
                <div key={pillar.pillarName} style={styles.pillarCard}>
                  <h3 style={{ marginBottom: '10px', color: '#0056b3' }}>{pillar.pillarName}</h3>
                  <div style={styles.pillarScore(pillar.score)}>{pillar.score}/100</div>
                  <p style={styles.pillarRating}>{pillar.rating}</p>
                  <p style={styles.pillarSummary}>{pillar.summary}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Diagnostics */}
          <div style={styles.resultsSection}>
            <h2 style={styles.resultsSectionTitle}>Performance Insights & Bottlenecks</h2>
            {reviewResults.performanceDiagnostics.map(diag => (
              <div key={diag.id} style={styles.findingItem}>
                <h3 style={styles.findingTitle}>{diag.title}</h3>
                <p style={styles.findingDescription}><strong>Description:</strong> {diag.description}</p>
                <p style={styles.findingDescription}><strong>Impact:</strong> {diag.impact}</p>
                <p style={styles.findingDescription}><strong>Recommendation:</strong> {diag.recommendation}</p>
                <p style={{ fontSize: '0.85em', color: '#888' }}>Related Pillar: {diag.pillarRelated}</p>
              </div>
            ))}
            {reviewResults.loadTestResults && (
              <div style={styles.findingItem}>
                <h3 style={styles.findingTitle}>Initial Load Time Test</h3>
                <p><strong>Response Time:</strong> {reviewResults.loadTestResults.responseTimeMs} ms</p>
                <p><strong>Geographical Impact:</strong> {reviewResults.loadTestResults.geographicalImpact}</p>
                <p><strong>Details:</strong> {reviewResults.loadTestResults.details}</p>
                <p style={{ fontSize: '0.85em', color: '#888' }}>Related Pillar: Performance Efficiency</p>
              </div>
            )}
          </div>

          {/* Architecture Diagram Suggestions */}
          <div style={styles.resultsSection}>
            <h2 style={styles.resultsSectionTitle}>Recommended Architecture Improvements</h2>
            <p style={{ marginBottom: '15px' }}><strong>Current Architecture:</strong> {reviewResults.architectureSuggestions.currentArchitectureDescription}</p>
            <p style={{ marginBottom: '15px' }}><strong>Recommendation:</strong> {reviewResults.architectureSuggestions.recommendedArchitectureDescription}</p>
            <ul style={{ listStyleType: 'disc', paddingLeft: '25px' }}>
              {reviewResults.architectureSuggestions.recommendationsList.map((rec, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>
                  {rec.description} (Suggested Services: {rec.awsServices.join(', ')})
                </li>
              ))}
            </ul>
          </div>

          {/* Risk/Impact Report */}
          <div style={styles.resultsSection}>
            <h2 style={styles.resultsSectionTitle}>Critical Findings & Business Impact</h2>
            {reviewResults.riskImpactReport.map(risk => (
              <div key={risk.id} style={styles.findingItem}>
                <h3 style={styles.findingTitle}>
                  <span style={risk.riskLevel === 'High' ? styles.riskLevelHigh : risk.riskLevel === 'Moderate' ? styles.riskLevelModerate : styles.riskLevelLow}>
                    {risk.riskLevel} Risk:
                  </span> {risk.finding}
                </h3>
                <p style={styles.findingDescription}><strong>Pillar Affected:</strong> {risk.pillarRelated}</p>
                <p style={styles.findingDescription}><strong>Business Impact:</strong> {risk.businessImpact}</p>
                <p style={styles.findingDescription}><strong>Benefit of Remediation:</strong> {risk.remediationBenefit}</p>
              </div>
            ))}
          </div>

          {/* Call to Action / Lead Capture */}
          <div style={{ textAlign: 'center', marginTop: '40px', padding: '20px', backgroundColor: '#e9f7ef', borderRadius: '8px' }}>
            <h2 style={{ color: '#28a745', marginBottom: '20px' }}>Take the Next Step Towards an Optimized AWS Environment</h2>
            <p style={{ fontSize: '1.1em', marginBottom: '25px', lineHeight: '1.5' }}>
              Your AWS environment has several high-risk issues identified. Don't leave your business vulnerable.
            </p>
            <button style={styles.ctaButton} onClick={() => setShowDownloadModal(true)}>
              Download Full Architecture Audit Report
            </button>
            <button style={{ ...styles.ctaButton, backgroundColor: '#007bff' }} onClick={() => setShowConsultationModal(true)}>
              Talk to an AWS Architect About Your Results
            </button>
          </div>
        </div>
      )}

      {/* Download Report Modal */}
      {showDownloadModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.closeButton} onClick={() => { setShowDownloadModal(false); setLeadFormStatus(null); }}>&times;</button>
            <h2 style={styles.modalTitle}>Download Your Full Report</h2>
            {leadFormStatus === 'success' ? (
              <p style={{ textAlign: 'center', color: '#28a745', fontSize: '1.1em' }}>
                Report sent to your email address! Check your inbox.
              </p>
            ) : (
              <form onSubmit={handleDownloadReport}>
                <p style={{ textAlign: 'center', marginBottom: '20px' }}>Enter your email to receive a detailed PDF report of your review findings.</p>
                <input
                  type="email"
                  placeholder="Your Email Address"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  style={styles.modalInput}
                  required
                />
                <button type="submit" style={styles.modalButton} disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Report'}
                </button>
                {isLoading && <p style={styles.smallText}>Please wait...</p>}
                {leadFormStatus === 'error' && <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>Failed to send report. Please try again.</p>}
              </form>
            )}
          </div>
        </div>
      )}

      {/* Consultation Modal */}
      {showConsultationModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.closeButton} onClick={() => { setShowConsultationModal(false); setLeadFormStatus(null); }}>&times;</button>
            <h2 style={styles.modalTitle}>Schedule a Free Consultation</h2>
            {leadFormStatus === 'success' ? (
              <p style={{ textAlign: 'center', color: '#28a745', fontSize: '1.1em' }}>
                Your request has been received! Our team will contact you shortly.
              </p>
            ) : (
              <form onSubmit={handleConsultationRequest}>
                <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                  Discuss your results with a certified AWS architect to address high-risk issues and optimize your setup.
                </p>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  style={styles.modalInput}
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email Address"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  style={styles.modalInput}
                  required
                />
                <input
                  type="tel"
                  placeholder="Your Phone Number (Optional)"
                  value={leadPhone}
                  onChange={(e) => setLeadPhone(e.target.value)}
                  style={styles.modalInput}
                />
                <textarea
                  placeholder="Any specific concerns you'd like to discuss? (Optional)"
                  value={leadMessage}
                  onChange={(e) => setLeadMessage(e.target.value)}
                  style={{ ...styles.modalInput, height: '80px', resize: 'vertical' }}
                />
                <button type="submit" style={styles.modalButton} disabled={isLoading}>
                  {isLoading ? 'Submitting...' : 'Request Consultation'}
                </button>
                {isLoading && <p style={styles.smallText}>Please wait...</p>}
                {leadFormStatus === 'error' && <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>Failed to submit request. Please try again.</p>}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;